import { Grid, styled } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import {
  Backdrop,
  HandGestureIcon,
  ISelectValue,
  NotificationText,
  Select,
  TextInput,
  Typography,
} from '@room-match/shared-ui-components';
import EthnicAppearances from 'data/EthnicAppearances.json';
import eyeColour from 'data/EyeColor.json';
import gender from 'data/Gender.json';
import hairColour from 'data/HairColor.json';
import hairLength from 'data/HairLength.json';
import months from 'data/Months.json';
import pronouns from 'data/Pronouns.json';
import { isEqual } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { COLORS } from 'shared/constants/COLORS';
import { DefinitionTitle } from 'shared/enums';
import { IGeneralSection } from 'shared/interfaces/IGeneral';
import { useAppSelector } from 'shared/redux/store';
import { talentService } from 'shared/services/talentService';
import { arrayToParagraphShortener } from 'shared/utils';
import { getMinorYears } from 'shared/utils/common';
import AdultSlider from './AdultSlider';
import DefinitionModals from './DefinitionModals/DefinitionModals';
import {
  CustomButton,
  CustomInputLabel,
  DoBContainer,
  FlexContainer,
  InstructionCont,
  MainCont,
  Row,
  SaveButton,
  SectionContainer,
  useStyles,
} from './GeneralAppearance.styles';
import MinorSlider from './MinorSlider';

const SpacedTitle = styled(Typography)(({ theme }) => ({
  marginRight: theme.spacing(1),
  color: theme.palette.type === 'dark' ? '#fff' : '#000',
}));

const generateDays = (n: number) => {
  const days = [...Array(n).keys()];
  const arr: ISelectValue[] = [];
  days.forEach((e) => arr.push({ key: `${e + 1}`, value: `${e + 1}` }));
  return arr;
};

const { getGeneral, updateTalent, updateMe } = talentService();

const GeneralAppearance = () => {
  const classes = useStyles();
  const { user: userState } = useAppSelector((state) => state);
  const [_, setSliderValue] = useState<number[]>([20, 40]);
  const [definitionSelected, setDefinitionSelected] = useState<DefinitionTitle>(DefinitionTitle.Tattoo);
  const [openDefinitionModal, handleOpenDefinitionModal] = useState<boolean>(false);
  const { data: generalData, isLoading: isGeneralLoading } = getGeneral();
  const { mutateAsync: talentUpdate, isLoading: isGeneralSaving } = updateTalent();
  const { mutateAsync: meUpdate, isLoading: isMeSaving } = updateMe();
  const queryClient = useQueryClient();
  const [meInfo, setmeInfo] = useState({
    gender: '',
    identifies_as: '',
  });

  const [generalInfo, setGeneralInfo] = useState<IGeneralSection>({
    ethnic_appearance_primary: '',
    ethnic_appearance_secondary: '',
    ethnic_appearance_other: '',
    hair_colour: '',
    eye_colour: '',
    hair_length: '',
    tattoo: [],
    piercings: [],
    facial_hair: [],
    physical_attributes: [],
    twin_triplet: [],
  });
  const [birth, setBirth] = useState({
    day: '',
    month: '',
    year: '',
  });
  const [days, setDays] = useState(generateDays(31));
  const [val, setVal] = useState<number[] | null>(null);
  const [showSuccess, handleShowSuccess] = useState<boolean>(false);
  const [rawData, setRawData] = useState<any>({});
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);

  const is31 = (month: string) => {
    return (
      month === '01' ||
      month === '03' ||
      month === '05' ||
      month === '07' ||
      month === '08' ||
      month === '10' ||
      month === '12'
    );
  };

  useEffect(() => {
    //checking leap year
    if (birth.month === '02') {
      if (parseInt(birth.year) % 4 === 0) {
        if (parseInt(birth.day) > 29) {
          setBirth((b) => ({ ...b, day: '29' }));
        }
        setDays(generateDays(29));
      } else {
        if (parseInt(birth.day) > 28) {
          setBirth((b) => ({ ...b, day: '28' }));
        }
        setDays(generateDays(28));
      }
    } else {
      if (is31(birth.month)) {
        setDays(generateDays(31));
      } else {
        if (birth.day === '31') {
          setBirth((b) => ({ ...b, day: '30' }));
        }
        setDays(generateDays(30));
      }
    }
  }, [birth]);

  const getAllowance = (age: number) => {
    if (age >= 3 && age <= 5) {
      return 1;
    } else if (age >= 6 && age <= 10) {
      return 2;
    }
    return 3;
  };

  const minorDefault = () => {
    const me = userState?.user?.attributes;
    if (me?.adult_minor.toLowerCase() === 'adult') return;
    const diff = new Date().getFullYear() - parseInt(me?.birth_date?.split('/')[2] || '0');
    const allowance = getAllowance(diff);

    const min = diff - allowance;
    const max = diff + allowance;
    let merawdata: any = {};

    if (allowance === 1) {
      setVal([min, max]);
      merawdata = {
        age_range_from: min,
        age_range_to: max,
      };
    } else {
      setVal([min + 1, max - 1]);
      merawdata = {
        age_range_from: min + 1,
        age_range_to: max - 1,
      };
    }

    setRawData((m: any) => ({
      ...m,
      meData: {
        ...(m?.meData || {}),
        ...merawdata,
      },
    }));
  };

  useEffect(() => {
    const me = userState?.user?.attributes;

    if (!me) return;
    const { gender, identifies_as, adult_minor, birth_date, age_range_from, age_range_to } = me;

    setmeInfo((i) => ({ ...i, gender, identifies_as }));
    let merawdata: any = {
      gender,
      identifies_as,
    };

    if (me.birth_date) {
      const birthSplit = me.birth_date?.split('/');
      setBirth({
        day: parseInt(birthSplit[0]) > 9 ? birthSplit[0] : birthSplit[0].substring(1),
        month: birthSplit[1],
        year: birthSplit[2],
      });
    }

    if (adult_minor.toLowerCase() === 'adult') {
      if (age_range_from && age_range_to && age_range_from > 15 && age_range_to < 110) {
        setVal([age_range_from, age_range_to]);
        merawdata = {
          ...merawdata,
          age_range_from,
          age_range_to,
        };
      } else {
        const min = 15;
        const max = 23;
        merawdata = {
          ...merawdata,
          age_range_from: min,
          age_range_to: max,
        };
        setVal([min, max]);
        // }
      }
    } else {
      if (age_range_from && age_range_to) {
        setVal([age_range_from, age_range_to]);
        merawdata = {
          ...merawdata,
          age_range_from,
          age_range_to,
        };
      } else {
        minorDefault();
      }
    }

    setRawData((m: any) => ({
      ...m,
      meData: {
        ...(m?.meData || {}),
        ...merawdata,
        birth_date: birth_date,
      },
    }));
  }, [userState]);

  useEffect(() => {
    const genData = generalData?.data.attributes.general;

    if (genData !== null) {
      const gendata = {
        ethnic_appearance_other: genData?.ethnic_appearance_other || '',
        ethnic_appearance_primary: genData?.ethnic_appearance_primary || '',
        ethnic_appearance_secondary: genData?.ethnic_appearance_secondary || '',
        hair_colour: genData?.hair_colour || '',
        hair_length: genData?.hair_length || '',
        eye_colour: genData?.eye_colour || '',
        tattoo: genData?.tattoo || [],
        piercings: genData?.piercings || [],
        facial_hair: genData?.facial_hair || [],
        physical_attributes: genData?.physical_attributes || [],
        twin_triplet: genData?.twin_triplet || [],
      };

      setGeneralInfo((i) => ({
        ...i,
        ...gendata,
      }));
      setRawData((m: any) => ({
        ...m,
        generalData: {
          ...m?.generalData,
          ...gendata,
        },
      }));
    }
  }, [generalData]);

  useEffect(() => {
    const birth_date = `${birth.day}/${birth.month}/${birth.year}`;

    const momentNow = moment(new Date());
    const birthMoment = moment(birth_date, 'DD/MM/YYYY');
    const duration = momentNow.diff(birthMoment, 'year');
    const isBaby = duration < 2;

    const mePayload = {
      ...meInfo,
      birth_date: (birth.day && birth.month && birth.year ? birth_date : null) as any,
      age_range_from: isBaby ? 0 : val ? val[0] : 0,
      age_range_to: isBaby ? 2 : val ? val[1] : 1,
    };

    setIsFormDirty(!isEqual(rawData, { meData: mePayload, generalData: generalInfo }));
  }, [JSON.stringify(meInfo), JSON.stringify(generalInfo), JSON.stringify(rawData), birth, val]);

  const handleSliderChange = (event: any, newValue: number | number[]) => {
    setSliderValue(newValue as number[]);
  };

  const handleAutocompleteChange = (field_key: string, val: string | null) => {
    setGeneralInfo((i) => ({ ...i, [field_key]: val }));
  };

  const handleMeChange = (e: any) => {
    setmeInfo((i) => ({ ...i, [e.target.name]: e.target.value }));
  };

  const birthChange = (e: any) => {
    const newBirth = { ...birth, [e.target.name]: e.target.value };
    const birth_date: any = `${
      newBirth.day.length === 1
        ? `0${newBirth.day}`
        : !is31(newBirth.month) && parseInt(newBirth.day) > 30
          ? newBirth.month === '02'
            ? '28'
            : '30'
          : newBirth.day
    }/${newBirth.month}/${newBirth.year}`;

    const now = moment(new Date());
    const birthMoment = moment(`${birth.month}/${birth.day}/${birth.year}`);
    const newBirthMoment = moment(
      `${newBirth.month}/${
        newBirth.day.length === 1
          ? `0${newBirth.day}`
          : !is31(newBirth.month) && parseInt(newBirth.day) > 30
            ? newBirth.month === '02'
              ? '28'
              : '30'
            : newBirth.day
      }/${newBirth.year}`,
    );
    const duration1 = now.diff(birthMoment, 'year');
    const duration2 = now.diff(newBirthMoment, 'year');

    //if year has changes update val
    if (duration1 !== duration2) {
      const allowance = getAllowance(duration1);

      const min = duration2 - allowance;
      const max = duration2 + allowance;

      if (allowance === 1) {
        setVal([min, max]);
      } else {
        setVal([min + 1, max - 1]);
      }

      setmeInfo((i) => ({
        ...i,
        birth_date,
        age_range_from: allowance === 1 ? min : min + 1,
        age_range_to: allowance === 1 ? max : max - 1,
      }));
    } else {
      setmeInfo((i) => ({ ...i, birth_date }));
    }
    setBirth((b) => ({ ...b, [e.target.name]: e.target.value }));
  };

  const saveDefinition = (info: IGeneralSection) => {
    setGeneralInfo((i) => ({ ...i, ...info }));
    handleOpenDefinitionModal(false);
  };

  const handleSaveGeneralAppearance = async () => {
    const birth_date = `${birth.day}/${birth.month}/${birth.year}`;

    const momentNow = moment(new Date());
    const birthMoment = moment(birth_date, 'DD/MM/YYYY');
    const duration = momentNow.diff(birthMoment, 'year');
    const isBaby = duration < 2;
    console.log({ isBaby });

    const mePayload = {
      ...meInfo,
      birth_date: (birth.day && birth.month && birth.year ? birth_date : null) as any,
      age_range_from: isBaby ? 0 : val ? val[0] : 0,
      age_range_to: isBaby ? 2 : val ? val[1] : 1,
    };
    const talentPayload = {
      general: {
        ...generalInfo,
      },
    };
    await meUpdate(
      {
        ...mePayload,
      },
      { onSuccess: () => queryClient.resetQueries(['me']) },
    );

    await talentUpdate(
      {
        ...talentPayload,
      },
      {
        onSuccess: () => queryClient.resetQueries(['talents/general']),
      },
    );
  };

  return (
    <>
      <MainCont>
        <p style={{ fontSize: 20 }}>General</p>
        {userState?.user && userState?.user?.attributes?.adult_minor?.toLowerCase() === 'adult' ? (
          <>
            <SectionContainer>
              <CustomInputLabel id="dateOfBirth">Date of Birth</CustomInputLabel>
              <DoBContainer>
                <Select
                  onChange={birthChange}
                  value={birth.day}
                  variant="outlined"
                  fullWidth
                  data={days}
                  // IconComponent={SelectArrowDownIcon}
                  className={classes.select}
                  placeHolder={'Select Day'}
                  name="day"
                />
                <Select
                  onChange={birthChange}
                  value={birth.month}
                  variant="outlined"
                  fullWidth
                  data={months}
                  // IconComponent={SelectArrowDownIcon}
                  className={classes.select}
                  placeHolder={'Select Month'}
                  name="month"
                />
                <Select
                  onChange={birthChange}
                  value={birth.year}
                  variant="outlined"
                  fullWidth
                  data={getMinorYears(101, 18)}
                  // IconComponent={SelectArrowDownIcon}
                  className={classes.select}
                  placeHolder={'Select Year'}
                  name="year"
                />
              </DoBContainer>
            </SectionContainer>
            <InstructionCont>
              <HandGestureIcon fill={COLORS.PRIMARY} />
              <p style={{ marginLeft: 20, wordBreak: 'break-word', fontSize: 16 }}>
                Drag the slider to your desired playing age range, then adjust the minimum and the maximum to suit.
              </p>
            </InstructionCont>
            {val ? <AdultSlider setVal={setVal} val={val} /> : <React.Fragment />}
          </>
        ) : (
          <>
            <SectionContainer>
              <CustomInputLabel id="dateOfBirth">Date of Birth</CustomInputLabel>
              <DoBContainer>
                <Select
                  onChange={birthChange}
                  value={birth.day}
                  variant="outlined"
                  fullWidth
                  data={days}
                  // IconComponent={SelectArrowDownIcon}
                  className={classes.select}
                  placeHolder={'Select Day'}
                  name="day"
                />
                <Select
                  onChange={birthChange}
                  value={birth.month}
                  variant="outlined"
                  fullWidth
                  data={months}
                  // IconComponent={SelectArrowDownIcon}
                  className={classes.select}
                  placeHolder={'Select Month'}
                  name="month"
                />
                <Select
                  onChange={birthChange}
                  value={birth.year}
                  variant="outlined"
                  fullWidth
                  data={getMinorYears(19)}
                  // IconComponent={SelectArrowDownIcon}
                  className={classes.select}
                  placeHolder={'Select Year'}
                  name="year"
                />
              </DoBContainer>
            </SectionContainer>
            {/* <AdultSlider age={age} /> */}
            <MinorSlider birth={birth} val={val} setVal={setVal} />
          </>
        )}

        <SectionContainer style={{ margin: '10px 0 20px 0' }}>
          <Row>
            <Grid item lg={4} md={4} xs={12}>
              <CustomInputLabel id="gender">Select Gender</CustomInputLabel>
              <Select
                variant="outlined"
                fullWidth
                data={gender}
                // IconComponent={SelectArrowDownIcon}
                className={classes.select}
                placeHolder={'Select'}
                onChange={handleMeChange}
                name="gender"
                value={meInfo.gender}
              />
            </Grid>
            <Grid item lg={4} md={4} xs={12}>
              <CustomInputLabel id="gender">Identifies As</CustomInputLabel>
              <Select
                variant="outlined"
                fullWidth
                data={pronouns}
                // IconComponent={SelectArrowDownIcon}
                className={classes.select}
                placeHolder={'Select'}
                onChange={handleMeChange}
                name="identifies_as"
                value={meInfo.identifies_as}
              />
            </Grid>
          </Row>
        </SectionContainer>
        <p style={{ fontSize: 20 }}>Appearance</p>
        <SectionContainer>
          <Row>
            <Grid item lg={4} md={4} xs={12}>
              <CustomInputLabel id="primaryEthnicAppearance">Ethnic Appearance (Primary)</CustomInputLabel>
              <Autocomplete
                onChange={(e, newValue) => {
                  handleAutocompleteChange('ethnic_appearance_primary', newValue);
                }}
                value={generalInfo.ethnic_appearance_primary}
                options={EthnicAppearances.map((i) => i.value)}
                getOptionSelected={(option, value) => option === value}
                fullWidth
                classes={{ option: classes.option }}
                renderInput={(params) => <TextInput placeholder={'Select'} {...params} />}
                disableClearable
              />
            </Grid>
            <Grid item lg={4} md={4} xs={12}>
              <CustomInputLabel id="secondaryEthnicAppearance">Ethnic Appearance (Secondary)</CustomInputLabel>
              <Autocomplete
                onChange={(e, newValue) => {
                  handleAutocompleteChange('ethnic_appearance_secondary', newValue);
                }}
                value={generalInfo.ethnic_appearance_secondary}
                options={EthnicAppearances.map((i) => i.value)}
                getOptionSelected={(option, value) => option === value}
                fullWidth
                classes={{ option: classes.option }}
                renderInput={(params) => <TextInput placeholder={'Select'} {...params} />}
              />
            </Grid>
            <Grid item lg={4} md={4} xs={12}>
              <CustomInputLabel id="otherEthnicAppearance">Ethnic Appearance (Other)</CustomInputLabel>
              <Autocomplete
                onChange={(e, newValue) => {
                  handleAutocompleteChange('ethnic_appearance_other', newValue);
                }}
                value={generalInfo.ethnic_appearance_other}
                options={EthnicAppearances.map((i) => i.value)}
                getOptionSelected={(option, value) => option === value}
                fullWidth
                classes={{ option: classes.option }}
                renderInput={(params) => <TextInput placeholder={'Select'} {...params} />}
              />
            </Grid>
          </Row>
        </SectionContainer>
        <SectionContainer>
          <Row>
            <Grid item lg={4} md={4} xs={12}>
              <CustomInputLabel id="eyeColour">Eye Colour</CustomInputLabel>
              <Autocomplete
                onChange={(e, newValue) => {
                  handleAutocompleteChange('eye_colour', newValue);
                }}
                value={generalInfo.eye_colour}
                options={eyeColour.map((i) => i.value)}
                getOptionSelected={(option, value) => option === value}
                fullWidth
                classes={{ option: classes.option }}
                renderInput={(params) => <TextInput placeholder={'Select'} {...params} />}
                disableClearable
              />
            </Grid>
            <Grid item lg={4} md={4} xs={12}>
              <CustomInputLabel id="hairColour">Hair Colour</CustomInputLabel>
              <Autocomplete
                onChange={(e, newValue) => {
                  handleAutocompleteChange('hair_colour', newValue);
                }}
                value={generalInfo.hair_colour}
                options={hairColour.map((i) => i.value)}
                getOptionSelected={(option, value) => option === value}
                fullWidth
                classes={{ option: classes.option }}
                renderInput={(params) => <TextInput placeholder={'Select'} {...params} />}
                disableClearable
              />
            </Grid>
            <Grid item lg={4} md={4} xs={12}>
              <CustomInputLabel id="hairLength">Hair Length</CustomInputLabel>
              <Autocomplete
                onChange={(e, newValue) => {
                  handleAutocompleteChange('hair_length', newValue);
                }}
                value={generalInfo.hair_length}
                options={hairLength.map((i) => i.value)}
                getOptionSelected={(option, value) => option === value}
                fullWidth
                classes={{ option: classes.option }}
                renderInput={(params) => <TextInput placeholder={'Select'} {...params} />}
                disableClearable
              />
            </Grid>
          </Row>
        </SectionContainer>
        <SectionContainer>
          <Row>
            <Grid item lg={4} md={4} xs={12}>
              <CustomInputLabel id="tattoos">Tattoos</CustomInputLabel>
              <CustomButton
                fullWidth
                onClick={() => {
                  setDefinitionSelected(DefinitionTitle.Tattoo);
                  handleOpenDefinitionModal(true);
                }}
              >
                {generalInfo.tattoo && generalInfo.tattoo.length !== 0 ? (
                  <>
                    <SpacedTitle>Tattoos: </SpacedTitle>
                    <Typography color="primary">{arrayToParagraphShortener({ lists: generalInfo.tattoo })}</Typography>
                  </>
                ) : (
                  <div>Define Tattoos</div>
                )}
              </CustomButton>
            </Grid>
            <Grid item lg={4} md={4} xs={12}>
              <CustomInputLabel id="piercings">Piercings</CustomInputLabel>
              <CustomButton
                fullWidth
                onClick={() => {
                  setDefinitionSelected(DefinitionTitle.Piercings);
                  handleOpenDefinitionModal(true);
                }}
              >
                {generalInfo.piercings && generalInfo.piercings.length !== 0 ? (
                  <>
                    <SpacedTitle>Piercings: </SpacedTitle>
                    <Typography color="primary">
                      {arrayToParagraphShortener({ lists: generalInfo.piercings })}
                    </Typography>
                  </>
                ) : (
                  <div>Define Piercings</div>
                )}
              </CustomButton>
            </Grid>
            <Grid item lg={4} md={4} xs={12}>
              <CustomInputLabel id="facialHair">Facial Hair</CustomInputLabel>
              <CustomButton
                fullWidth
                onClick={() => {
                  setDefinitionSelected(DefinitionTitle.FacialHair);
                  handleOpenDefinitionModal(true);
                }}
              >
                {generalInfo.facial_hair && generalInfo.facial_hair.length !== 0 ? (
                  <>
                    <SpacedTitle>Facial Hair: </SpacedTitle>
                    <Typography color="primary">
                      {arrayToParagraphShortener({ lists: generalInfo.facial_hair })}
                    </Typography>
                  </>
                ) : (
                  <div>Define Facial Hair</div>
                )}
              </CustomButton>
            </Grid>
          </Row>
        </SectionContainer>
        <SectionContainer>
          <Row>
            <Grid item lg={4} md={4} xs={12}>
              <CustomInputLabel id="physicalAttributes">Physical Attributes</CustomInputLabel>
              <CustomButton
                fullWidth
                onClick={() => {
                  setDefinitionSelected(DefinitionTitle.PhysicalAttributes);
                  handleOpenDefinitionModal(true);
                }}
              >
                {generalInfo.physical_attributes && generalInfo.physical_attributes.length !== 0 ? (
                  <>
                    <SpacedTitle>Physical Attributes:</SpacedTitle>
                    <Typography color="primary">
                      {arrayToParagraphShortener({ lists: generalInfo.physical_attributes })}
                    </Typography>
                  </>
                ) : (
                  <div>Define Physical Attributes</div>
                )}
              </CustomButton>
            </Grid>
            <Grid item lg={4} md={4} xs={12}>
              <CustomInputLabel id="identicalTwinOrTriplet">Identical Twin or Triplet</CustomInputLabel>
              <CustomButton
                fullWidth
                onClick={() => {
                  setDefinitionSelected(DefinitionTitle.TwinTriplet);
                  handleOpenDefinitionModal(true);
                }}
              >
                {generalInfo.twin_triplet && generalInfo.twin_triplet.length !== 0 ? (
                  <>
                    <SpacedTitle>Twin and/or Triplet:</SpacedTitle>
                    <Typography color="primary">
                      {arrayToParagraphShortener({ lists: generalInfo.twin_triplet })}
                    </Typography>
                  </>
                ) : (
                  <div>Define Twin and/or Triplet</div>
                )}
              </CustomButton>
            </Grid>
            <Grid item lg={4} md={4} xs={12}></Grid>
          </Row>
        </SectionContainer>
        <SectionContainer>
          <FlexContainer $gap={20}>
            <SaveButton
              color="primary"
              variant="contained"
              disableElevation
              onClick={() => handleSaveGeneralAppearance()}
              disabled={!isFormDirty}
            >
              Save Changes
            </SaveButton>
            <NotificationText show={showSuccess} setShow={handleShowSuccess} />
          </FlexContainer>
        </SectionContainer>
        {openDefinitionModal ? (
          <DefinitionModals
            setInfo={saveDefinition}
            generalInfo={generalInfo}
            open={openDefinitionModal}
            onClose={handleOpenDefinitionModal}
            definition_title={definitionSelected}
            saving={isGeneralSaving}
          />
        ) : (
          <></>
        )}
      </MainCont>
      <Backdrop isLoading={isGeneralLoading || isMeSaving || isGeneralSaving || false} />
    </>
  );
};

export default GeneralAppearance;
